import React, { useRef, useMemo } from "react";
import useHubConnection from "./useHubConnection";
import useJWTCookie from "./useJWTCookie";
import CONSTANTS from "../../contracts/Constants";
import { useUserStore } from "../../stores/useUserStore";
import { defaultUser } from "../../stores/useUserStore";

type UseOnNewMeetingProps = {
    onMeetingChange: () => void;
};

const useOnNewMeeting = ({ onMeetingChange }: UseOnNewMeetingProps) => {
    const { getJwt } = useJWTCookie();
    const user = useUserStore((state) => state.user);

    // ✅ 新增：仅在用户已认证时连接（user.id 不是默认值）
    const isAuthenticated = user.id !== defaultUser.id;

    /**
     * 获取有效的JWT令牌
     */
    const getAccessToken = useRef(async (): Promise<string> => {
        try {
            const [jwtToken, expirationDateString] = await getJwt();
            
            console.log(`[OnNewMeeting] getJwt returned - token exists: ${!!jwtToken}, expiration: ${expirationDateString}`);
            
            if (!jwtToken || jwtToken.trim() === "") {
                console.warn("[OnNewMeeting] ⚠️ No JWT token found in storage - user may not be logged in");
                return "";
            }
            
            // 检查令牌是否过期
            const expirationDate = expirationDateString ? parseInt(expirationDateString as any) : null;
            const now = new Date().getTime();
            
            if (expirationDate && now > expirationDate) {
                console.warn(`[OnNewMeeting] ⚠️ JWT token has expired - expiration: ${expirationDate}, now: ${now}`);
                return "";
            }
            
            return jwtToken;
        } catch (error) {
            console.error("[OnNewMeeting] ❌ Failed to retrieve JWT token:", error);
            return "";
        }
    }).current;

    /**
     * 缓存 useHubConnection 的配置选项
     */
    const hubConnectionOptions = useMemo(() => ({
        getAccessToken: getAccessToken
    }), [getAccessToken]);

    /**
     * 缓存连接回调函数
     */
    const onConnectionMemo = useMemo(() => (hubConnection: any) => {
        hubConnection.on("MeetingStarted", onMeetingChange);
        hubConnection.on("MeetingEnded", onMeetingChange);
    }, [onMeetingChange]);

    // ✅ 关键修复：始终调用 useHubConnection，但通过 enabled 参数控制
    // 这样保持 hooks 调用顺序一致，符合 React hooks 规则
    useHubConnection(
        CONSTANTS.SocketUrl + `?meetingId=All`,
        onConnectionMemo,
        () => {},
        undefined,
        hubConnectionOptions,
        isAuthenticated  // ✅ 仅在已认证时连接
    );
};

export default useOnNewMeeting;
